import { makeAutoObservable, runInAction } from 'mobx';
import services from "../services";
import { extractErrorMessage } from "../utils/helpers";
import { generateCreteEntity, generateDeleteEntity, generateLoadEntity, generateLoadList } from "../utils/mobx";
import keyBy from "lodash/keyBy";
import createBillingInfoForm from "../forms/enrollBillingInfo";
import createBillingAddressForm from "../forms/billingAddress";
import {STARFISH_KIT_EN_SKU, STARFISH_KIT_ES_SKU, STARFISH_KIT_FR_SKU} from "../utils/constants";


const permanentItems = [, STARFISH_KIT_EN_SKU, STARFISH_KIT_FR_SKU, STARFISH_KIT_ES_SKU ];
const multiOptionsGroups = { };
const additionalWarningItems = [];

function canDeleteAdapter(cart) {
    if (!cart) return;
    let items = Array.isArray(cart) ? cart : cart.cartItems;
    if (items)
        items.forEach(x => {
            x.canDelete = !permanentItems.includes(x.productSKU);

            x.additionalWarning = !!additionalWarningItems.find(y => x.productSKU && x.productSKU.startsWith(y));
        });
    return cart;

}

function joinMultiOptions(items) {
    const removed = []
    items.forEach(x => {
        if (multiOptionsGroups[x.productSKU]) {
            const subItemsSKUs = multiOptionsGroups[x.productSKU];
            const subItems = items.filter(y => subItemsSKUs.includes(y.productSKU));
            if (subItems.length > 0) {
                if (!x.alternatives) x.alternatives = [{ ...x }];
                subItems.forEach(subItem => {
                    x.alternatives.push(subItem);
                    removed.push(subItem.productSKU);
                })
            }
        }
    })
    return items.filter(x => !removed.includes(x.productSKU));
}


function combineItemsWithRecomendations(cart, additionalItems, occurrence, isStarfishSignup = false) {
    let items = cart ? cart.cartItems.filter(x => x.occurrence === occurrence) : [];

    return items;
}

class EnrollmentStore {
    constructor(userBillingInfoStore, authStore, commonStore) {
        makeAutoObservable(this);
        this.authStore = authStore;
        this.commonStore = commonStore;
        this.userBillingInfoStore = userBillingInfoStore;
    }
    referralInfo = null;
    additionalItems = null;
    loadingReferralInfo = false;
    loadingAdditionalItems = false;
    loadingCartInfo = false;
    loadingCartTotals = false;
    loadingRenewalCartInfo = false;
    addingItemToCart = false;
    creating = false;
    cart = null;
    renewalCart = null;
    totals = null;
    expired = false;
    activeStep = "1";
    product = "";
    //forms:
    billingInfoForm = null;
    billingInfoFormSubmitting = false;

    billingAddressForm = null;
    billingAddressFormSubmitting = false;


    billingInfoFormEdit = null;
    billingInfoFormEditSubmitting = false;

    billingAddressFormEdit = null;
    billingAddressFormEditSubmitting = false;

    countries = [];
    loadingCountries = false;
    effectiveProductSKU = null;
    paymentID = 0;

    get oneTimeItems() {
        return combineItemsWithRecomendations(this.cart, this.additionalItems, "onetime");
    }

    get recurringItems() {
        return combineItemsWithRecomendations(this.cart, this.additionalItems, "recurring", this.isStarfishSignup);
    }

    get cartTotal() {
        return this.totals && this.totals[0].cartTotal;
    }

    get effectiveAdditionalItems() {
        if (!this.cart || !this.additionalItems) return null;
        let existing = keyBy([...this.oneTimeItems, ...this.recurringItems], "productSKU");
        const additionalItems = this.additionalItems.filter(x => !existing[x.productSKU]);
        if (this.isStarfishSignup === true) {
            return additionalItems.filter(x => x.productSKU.startsWith('eACCELERATOR') || x.productSKU.startsWith('eStarfishSub'));
        } else {
            return additionalItems.filter(x => !x.productSKU.startsWith('eStarfishSub'));
        }
    }

    setPaymentId(newVal) {
        this.paymentID = newVal;
    }

    get isStarfishSignup() {
        return this.product?.includes('starfish') || this.product?.includes('topcustomer') ? true : false;
    }

    setProduct(product) {
        this.product = product;
    }

    initBillingInfoForm() {

        let country = this.authStore.currentUser.address && this.authStore.currentUser.address.country;
        this.billingInfoForm = createBillingInfoForm({
            paymentID: 0,
            billCountry: country,
            billFirstName: this.authStore.currentUser.firstName,
            billLastName: this.authStore.currentUser.lastName
        }, {
            onSuccess: async (form) => { 
                try {
                    // Save the payment method to the user's account
                    const values = form.values();
                    const result = await services.UserProfile.BillingService().create({
                        cardType: values.cardType,
                        cardNumber: values.cardNumber,
                        expMonth: values.cardExpMonth,
                        expYear: values.cardExpYear,
                        cvv: values.cardCode,
                        firstName: values.billFirstName,
                        lastName: values.billLastName,
                        address1: values.billAddress,
                        address2: "",
                        city: values.billCity,
                        region: values.billRegion,
                        zip: values.billPostalCode,
                        country: values.billCountry,
                        primary: false
                    });
                    
                    // Reload the cards list
                    await this.userBillingInfoStore.loadAll();
                    
                    // Preselect the newly added payment method
                    if (result && result.paymentID) {
                        this.setPaymentId(result.paymentID);
                    }
                    
                    runInAction(() => { 
                        this.billingInfoFormSubmitting = false; 
                        this.handleNextStep(); 
                    }); 
                } catch (error) {
                    runInAction(() => { 
                        this.billingInfoFormSubmitting = false;
                        this.setError(error);
                    });
                }
            },
            onError: () => { runInAction(() => { this.billingInfoFormSubmitting = false; }); }
        });
    }

    async initBillingInfoFormEdit(onSuccess, existingCard = null) {
        // Store the current paymentID before attempting to save
        const previousPaymentID = this.paymentID;
        const isEditMode = !!existingCard;
        
        // Prepare initial values from existing card if editing
        const initialValues = existingCard ? {
            billCountry: existingCard.country || this.authStore.currentUser.address?.country,
            billFirstName: existingCard.firstName || this.authStore.currentUser.firstName,
            billLastName: existingCard.lastName || this.authStore.currentUser.lastName,
            billAddress: existingCard.address || '',
            billCity: existingCard.city || '',
            billRegion: existingCard.region || '',
            billPostalCode: existingCard.zip || '',
            cardType: existingCard.type || 'Visa',
            cardNumber: '', // Never populate card number for security
            cardExpMonth: existingCard.expMonth ? parseInt(existingCard.expMonth) : '',
            cardExpYear: existingCard.expYear ? parseInt(existingCard.expYear) : '',
            cardCode: '' // Never populate CVV for security
        } : {};
        
        this.billingInfoFormEdit = createBillingInfoForm(initialValues
            , {
                onSuccess: async (form) => {
                    try {
                        const values = form.values();
                        
                        // Prepare the DTO for create or update
                        const dto = {
                            cardType: values.cardType,
                            cardNumber: values.cardNumber,
                            expMonth: values.cardExpMonth,
                            expYear: values.cardExpYear,
                            cvv: values.cardCode,
                            firstName: values.billFirstName,
                            lastName: values.billLastName,
                            address1: values.billAddress,
                            address2: "",
                            city: values.billCity,
                            region: values.billRegion,
                            zip: values.billPostalCode,
                            country: values.billCountry,
                            primary: existingCard?.primary || false
                        };
                        
                        let result;
                        if (isEditMode) {
                            // Update existing card
                            result = await services.UserProfile.BillingService().update({
                                ...dto,
                                paymentID: existingCard.paymentID
                            });
                        } else {
                            // Create new card
                            result = await services.UserProfile.BillingService().create(dto);
                        }

                        // Reload the cards list
                        await this.userBillingInfoStore.loadAll();

                        // Preselect the payment method (new or edited)
                        if (result && result.paymentID) {
                            this.setPaymentId(result.paymentID);
                        } else if (isEditMode) {
                            this.setPaymentId(existingCard.paymentID);
                        }

                        runInAction(() => {
                            this.billingInfoFormEditSubmitting = false;
                            onSuccess();
                        });
                    } catch (error) {
                        runInAction(() => {
                            this.billingInfoFormEditSubmitting = false;
                            // Restore the previous paymentID on error
                            this.setPaymentId(previousPaymentID);
                            this.setError(error);
                        });
                    }
                },
                onError: () => { 
                    runInAction(() => { 
                        this.billingInfoFormEditSubmitting = false;
                        // Restore the previous paymentID on validation error
                        this.setPaymentId(previousPaymentID);
                    }); 
                }
                ,
            });
    }

    initBillingAddressFormEdit(onSuccess) {
        this.billingAddressFormEdit = createBillingAddressForm({ ...this.billingAddressForm.values() }
            , {
                onSuccess: () => {
                    this.billingAddressFormEditSubmitting = false;
                    if (this.billingAddressFormEdit)
                        this.billingAddressForm.update(this.billingAddressFormEdit.values());
                    onSuccess();
                },
                onError: () => { this.billingAddressFormEditSubmitting = false; }
                ,
            });
    }



    submitBillingInfoForm() {
        if (this.paymentID > 0 || this.paymentID === "ewallet") {
            this.handleNextStep();
            this.billingInfoForm.clear();
            this.billingInfoForm.reset();
        }
        else {

            this.billingInfoFormSubmitting = true;
            this.billingInfoForm.submit();
        }
    }
    submitBillingInfoEditForm() {
        this.billingInfoFormEditSubmitting = true;
        this.billingInfoFormEdit.submit();
    }
    submitBillingAddressForm() {
        this.billingAddressFormSubmitting = true;
        this.billingAddressForm.submit();
    }
    submitBillingAddressEditForm() {
        this.billingAddressFormEditSubmitting = true;
        this.billingAddressFormEdit.submit();
    }

    closePersonalFormEdit() {
        this.personalFormEdit = null;
    }
    closeBillingInfoFormEdit() {
        this.billingInfoFormEdit = null;
    }
    closeBillingAddressFormEdit() {
        this.billingAddressFormEdit = null;
    }

    async removeAlternative(item, alternativeSKU, country) {
        const selectedItem = this.cart.cartItems.find(x => x.productSKU === (item.effectiveProductSKU || item.productSKU))
        if (selectedItem) {

            await this.removeFromCart({
                cartID: this.cart.cartID,
                productID: selectedItem.productID,
                lang: this.commonStore.language
            });
            runInAction(() => {
                this.effectiveProductSKU = null
            })

            this.refreshItems({ cartID: this.cart.cartID, country, lang: this.commonStore.language })
        }
    }

    async switchAlternative(item, alternativeSKU, country) {
        const alternative = item.alternatives.find(x => x.productSKU === alternativeSKU);
        const selectedItem = this.cart.cartItems.find(x => x.productSKU === (item.effectiveProductSKU || item.productSKU))

        if (selectedItem) {
            await this.removeFromCart({
                cartID: this.cart.cartID,
                productID: selectedItem.productID,
                lang: this.commonStore.language
            });
        }

        try {
            await this.addToCart({
                cartID: this.cart.cartID,
                productID: alternative.productID,
                lang: this.commonStore.language,
                qty: 1
            });

            runInAction(() => {
                item.effectiveProductSKU = alternative.productSKU
                item.effectiveProductPrice = alternative.productPrice
                item.effectiveProductID = alternative.productID
                this.effectiveProductSKU = alternative.productSKU;
            })
        }
        catch (e) {

        }
        finally {

            //DEBUG: enforce enroll04 logic without server support
            // if ( alternative.productID === 20756 ){
            //     let enroll03 = this.cart.cartItems.find(x => x.productSKU === "enroll03" )
            //     if ( enroll03) {
            //         this.cart.cartItems.remove(enroll03)
            //     }
            //     this.cart.cartItems.unshift( {"productID":20756,"productSKU":"enroll04","productTitle":"Wallet Fast Funding","productDescription":"&nbsp;Fund your wallet immediately with $50 so you can start earning Starfish Perks.&nbsp; Funds are available for use as soon as you complete Identity Verification.<br>","productSummary":"","imageURL":"https://resources.lifeinfoapp.com/dnn/sc_images/starfish_black.jpg","quantity":1,"productPrice":"$100.00","productTC":0,"productPV":"0.00","totalCost":100,"totalPV":"0.00","totalTCs":"0.00","isDigital":1,"occurrence":"onetime","sortOrder":0})
            // }
            // else

            this.refreshItems({ cartID: this.cart.cartID, country, lang: this.commonStore.language })

        }

    }

    resetCart() {
        //this.cart = null
        //this.totals = null
    }
    setError(error, type = "series") {
        error = extractErrorMessage(error);
        if (error && type === "verify") {
            this.expired = true;
        }
        this.error = error;
    }

    setActiveStep(activeStep) {
        this.activeStep = activeStep;
    }

    get havePhysicalItems() {
        return this.cart && this.cart.cartItems && this.cart.cartItems.some(x => !x.isDigital)
            && !this.product.startsWith("starfish.kitmember")
            && !this.product.startsWith("starfish.kitupline")
    }

    handleNextStep() {
        let step = Math.min(3, Number(this.activeStep) + 1)

        this.setActiveStep(step.toString());
    }

    handlePrevStep() {
        let step = Math.max(1, Number(this.activeStep) - 1);
        this.setActiveStep(step.toString());
    }

    loadCountries = generateLoadList("countries", this, "loadingCountries"
        , () => { return services.Enrollment.listCountries(); }, "countries", "countries");

    verify = generateLoadEntity("verify", this, "loadingReferralInfo"
        , (loginGuid) => { return services.Enrollment.verify(loginGuid); }, "referralInfo");

    loadCart = generateLoadEntity("loadCart", this, "loadingCartInfo"
        , ({ product, lang, country }) => {
            return services.Enrollment.cart(product, lang, country);
        }, "cart", null, canDeleteAdapter);

    loadCartTotal = generateLoadEntity("loadCartTotals", this, "loadingCartTotals"
        , ({ cartGuid, lang, country, product, loginGuid }) => {
            return services.ShoppingCart.getCheckoutCartTotals(
                 {cartGuid, country, product, referralID:loginGuid });
        }, "totals");

    create = generateCreteEntity("create", this, "creating"
        , (params) => { return services.ShoppingCart.checkout(params); }, null, { throwError: true });

    refreshItems = generateLoadEntity("refreshItems", this, "loadingCartInfo"
        , ({ cartID, lang, country }) => { return services.Enrollment.loadItems(cartID, country, lang); }
        , (result) => {
            if (this.cart) {
                if (this.cart.cartItems)
                    this.cart.cartItems.replace(result);
                else
                    this.cart.cartItems = result;
            }
        }, null, canDeleteAdapter);
}

export default EnrollmentStore;